import { Observable, Subscription, Subject, of, forkJoin } from 'rxjs';
import { map, tap, flatMap } from 'rxjs/operators';

import {
  Vasat, VasatAPICall, VasatModel, VasatSearchQuery, SearchResults, User, VasatSession, VasatConnection,
  AsClass, AsToManyList, ToManyList, Role, AccessGroup, GUIDModel
} from "vasat"

/* TODO: update links to Vasat documentation. (find and replace: linkToVasat ) */

import { FourDService } from './4d-mapper.service';

let EXTENSIONS = {
  "fa-file-code-o": ["html", "xml", "js", "coffee", "scala", "java", "cpp", "cs", "h", "m", "sh", "bat"],
  "fa-file-pdf-o": ["pdf"],
  "fa-file-word-o": ["doc", "docx"],
  "fa-file-audio-o": ["wav", "mp3", "ogg"],
  "fa-file-video-o": ["avi", "mpg", "mkv"],
  "fa-file-image-o": ["png", "gif", "jpg", "jpeg", "tiff"],
  "fa-file-text-o": ["txt"]
}

let ignoreKeys = ['parent', 'account', 'folder', 'transform', 'dataType', 'fid']



export interface Quote{
  amount:number,
  unit:string
}


export interface AccountPlanImpl{
  maxStorage:number
  maxTeams:number
  maxProjects:number
  maxUsers:number
  price:number
  allAdjustments:{[ket:string]:number}
}

/**
 * <p>Reference to Coordinate Reference System (CRS). More details on EPSG can be found <a href="https://www.puzzlr.org/epsg-codes-explained/">here</a>.</p>
 */
export class EPSGCodes extends VasatModel<EPSGCodes>{
  constructor(V: VasatConnection, args?: any) {
    super(V, 'EPSGCodes', args)
  }

  uid: string
  CSRType: string
  areaCode: string

}


 
export class Widget extends VasatModel<Widget>{
  uid:string
  group:string
  features:string[]
  noteworthy:boolean
  
  constructor(V: VasatConnection, args?: any) {
    super(V, 'Widget', args)
  }
}


/**
 * <p>Defines the cost and limitations of the [[Account]].</p>
 */
export class AccountPlan extends VasatModel<AccountPlan>{


  cost: number
  maxUsers: number
  maxTeams: number
  maxStorage: number

  seq: number
  isTrial: boolean

  monthlyCredit:number
  
  @AsToManyList(Widget)
  availableWidgets:ToManyList<Widget>

  constructor(V: VasatConnection, args?: any) {
    super(V, 'AccountPlan', args)
  }
}

/**
 * <p>Client object that is used to get the token</p>
 */
export class Client extends VasatModel<Client>{
  token: String
  secret: string
  constructor(V: VasatConnection, args?: any) {
    super(V, 'Client', args)
  }
}


/**
 * <p>Account class that is used for access control.</p>
 */
export class Account extends VasatModel<Account>{
  uid: string
  demos: any[]
  tou: any

  isDomain:boolean

  widgets:string[]

  @AsClass(AccountPlan)
  plan: AccountPlan

  @AsClass(Account)
  parent: Account

  @AsToManyList(AccessGroup)
  accountGroups: ToManyList<AccessGroup>

  @AsToManyList(User)
  invitedUsers: ToManyList<User>

 
  canWrite: boolean

  autoCredit:number
  adhocCredit:number
  planToDate:number
  
  closed?:boolean

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Account', args)
  }


  GUID(){
    return this.uid;
  }
  GUID_FIELD_NAME(){
    return 'uid'
  }
  /**
   * <p>Updates values of this object with parameter object values.</p>
   * @param a The object that you want to copy values of.
   */
  set(a: any) {
    if (a && a.permissions) {
      this.canWrite = a.permissions.find(p => p == 'api_update')
    }
    else {
      this.canWrite = false
    }
    return super.set(a)
  }

  /**
   * <p>Parse this object to JSON format.</p>
   */
  toJSON(): any {
    let o = super.toJSON()
   // delete o.permissions
    delete o.canWrite
    return o
  }

  /**
   * <p>Checks if the account has the passed API type enabled.</p>
   * @param apiType 
   */
  hasApiEnabled(apiType: string): boolean {
    return this[apiType]
  }

  /**
   * <p>Enable/Disable API type</p>
   * @param apiType The API type you want to manipulate
   * @param enable True = Enable, False = Disable
   */
  enableDisableApi(apiType: string, enable: boolean): Observable<Account> {
    this[apiType] = enable
    return this.saveObservable()
  }

  accountPlanDetails(p:AccountPlan = this.plan){
    return this.vasat.api('/api/Account/' + this.id + '.plan?planId=' + p.id).pipe(map(r => <AccountPlanImpl>r))
  }
  previewPlan(sliders:AccountPlanImpl, p:AccountPlan){
    return this.vasat.api('/api/Account/' + this.id + '.plan?planId=' + p.id,'post',sliders).pipe(map(r => <AccountPlanImpl>r))
  }
  updatePlan(sliders:AccountPlanImpl, p:AccountPlan){
    
    return this.vasat.api('/api/Account/' + this.id + '.plan?planId=' + p.id,'put',sliders)
      .pipe(flatMap(_ => this.fetchObservable()))
  }
  buyCredit(amount:Number){
    return this.vasat.api('/api/Account/' + this.id + '.plan/' + amount,'post',{}).pipe(flatMap(_ => this.fetchObservable()))
  }
}

/**
 * <p>AccountableModel is a model with an [[Account]], so that they are not accessible to the public.</p>
 */
export class AccountableModel<T extends AccountableModel<T>> extends VasatModel<T>{
  @AsClass(Account)
  account: Account

  canWrite: boolean
  canDelete: boolean

  /**
   * <p>Retrieves this object</p>
   */
  fetchObservable() {
    var res = this.vasat.searchByObjectString(this.className()).equals('id', this.id).queryObservable().pipe(
      map(res => this.set(res.items[0].toJSON()))
    )
    return <any>res
  }

  /**
   * <p>Updates values of this object with parameter object values.</p>
   * @param a The object that you want to copy values of.
   */
  set(a: any) {
    if (a && a.permissions) {
      this.canWrite = a.permissions.find(p => p == 'api_update')
      this.canDelete = a.permissions.find(p => p == 'api_delete')
    }
    else {
      this.canWrite = false
      this.canDelete = false
    }
    return super.set(a)
  }

  /**
   * <p>Parse this object to JSON format.</p>
   */
  toJSON(): any {
    let o = super.toJSON()
    delete o.permissions
    return o
  }

}

export class Tag extends AccountableModel<Tag>{

  category: string
  rank: number
  colour: string
  date: number


  constructor(V: VasatConnection, args?: any) {
    super(V, 'Tag', args)
  }
}


/**
 * <p>ProcessorAPIs are very similar to [[TransformType]]s in that they describe the process that a [[Dataset]] will undergo, however ProcessorAPIs are third party processes that need to be set up.
 * These ProcessorAPIs are linked to the account.</p>
 */
export class ProcessorAPI extends AccountableModel<ProcessorAPI>{

  needsInput: boolean
  type: string
  url: string
  processorImpl: string
  qMap: any

  constructor(V: VasatConnection, args?: any) {
    super(V, 'ProcessorAPI', args)
  }
}


/**
 * <p>Defines the type of transformation that a [[Dataset]] object will undergo.</p>
 */
export class TransformType extends VasatModel<TransformType>{

  uid: string

  category:string
  pluginType?:string
  icon:string
  
  @AsToManyList(ProcessorAPI)
  apis: ToManyList<ProcessorAPI>

  //  @AsClass(DatasetType)
  createType: DatasetType

  @AsClass(TransformType)
  isSubTransform: TransformType

  needsCRS:boolean

  isFolder: boolean

  needsInput:boolean

  qMap:any[]
  
  constructor(V: VasatConnection, args?: any) {
    super(V, 'TransformType', args)
  }
  GUID(){
    return this.uid;
  }
  GUID_FIELD_NAME(){
    return 'uid'
  }

}

/**
 * <p>PlottableModel's are models/images that can be added to and displayed on the map.</p>
 */
export class PlottableModel<T extends PlottableModel<T>> extends AccountableModel<T>{

  llat: number
  llng: number
  lheight: number

  ulat: number
  ulng: number
  uheight: number

  latitude: number
  longitude: number
  height: number

  pitch: number
  heading: number
  roll: number

  vertical_offset: number

  matrix: number[]

  rawType: string

  @AsClass(TransformType)
  transformType: TransformType

  /**
   * <p>Gets <a href="https://cesiumjs.org/Cesium/Build/Documentation/Rectangle.html">Cesium Rectangle</a> of this PlottableModel object.</p>
   */
  rectangle() {
    var Cesium = window['Cesium']

    return Cesium.Rectangle.fromDegrees(this.llng, this.llat, this.ulng, this.ulat)
  }

  /**
   * <p>Gets <a href="https://cesiumjs.org/Cesium/Build/Documentation/Cartographic.html">Cesium Cartographic</a> from longitude and latitude specified in degrees</p>
   */
  cartographic() {
    var Cesium = window['Cesium']
    return Cesium.Cartographic.fromDegrees(this.llng, this.llat, this.lheight ? this.lheight : this.uheight)
  }

  /**
   * <p>Creates a new <a href="https://cesiumjs.org/Cesium/Build/Documentation/Cartesian3.html">Cesium Cartesian3</a> instance from a Cartographic input.</p>
   */
  catresian() {
    var Cesium = window['Cesium']
    return Cesium.Cartographic.toCartesian(this.cartographic())
  }
}

/**
 * <p>DatasetType is used to associate the [[Dataset]] with the [[TransformType]]. When a file is uploaded, the type of file should be auto-detected to determine the necessary [[TransformType]]s to be added in the appropriate lists ([[adhocTransforms]]/[[autoApplyTransforms]]).</p>
 */
export class DatasetType extends AccountableModel<DatasetType>{

  uid: string
  extension: string
  canPlot: boolean
  canDownload: boolean

  /**
   * <p>Custom TransformTypes that may be applied to a [[Dataset]].</p>
   */
  @AsToManyList(TransformType)
  adhocTransforms: ToManyList<TransformType>

  /**
   * <p>The [[TransformType]] to auto-apply when the Dataset is detected.</p>
   */
  @AsToManyList(TransformType)
  autoApplyTransforms: ToManyList<TransformType>

  _adhocTransforms: TransformType[]

  constructor(V: VasatConnection, args?: any) {
    super(V, 'DatasetType', args)
  }

  set(a){
    super.set(a)
    if(a._adhocTransforms){
       this._adhocTransforms = (<number[]>a._adhocTransforms).map(id => this.vasat.findCached(TransformType,id))
    }
    return this
  }
  /**
   * <p>Parse this object to JSON format.</p>
   */
  toJSON() {
    var v = super.toJSON()
    delete v._adhocTransforms
    return v
  }

  GUID(){
    return this.uid;
  }
  GUID_FIELD_NAME(){
    return 'uid'
  }

}


/**
 * <p>Folders contain the [[Dataset]]s or [[Transform]]s.</p>
 */
export class Folder extends AccountableModel<Folder> implements GUIDModel{
  @AsClass(Folder)
  parent: Folder

  forJobBatch: JobBatch

  forScene: TransformGroup

  forProject: Project
  /**
   * @depricated
   */
  @AsClass(TransformType)
  transform: TransformType

  linkedFiles:ToManyList<Dataset> = new ToManyList(Dataset,this,'linkedFiles') 

  fid: string
  root: any
  uid: string

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Folder', args)
  }
  GUID(): string {
   return this.uid
  }
  GUID_FIELD_NAME(): string {
    return 'uid'
  }

  /**
   * <p>Updates values of this object with parameter object values.</p>
   * @param a The object that you want to copy values of.
   */
  set(a) {
    super.set(a)
    // cant get around declration order for @AsClass so resorting to manual
    if(a.forJobBatch)
      this.forJobBatch = this.vasat.findCached(JobBatch,a.forJobBatch.id) || a.forJobBatch

    if(a.forScene)
      this.forScene = this.vasat.findCached(TransformGroup,a.forScene.id) || a.forScene

    if(a.forProject)
      this.forProject = this.vasat.findCached(Project,a.forProject.id) || a.forProject

      
    this.fid = "folder_" + this.id
    return this
  }

  /**
   * <p>Parse this object to JSON format.</p>
   */
  toJSON() {
    return super.toJSON()
  }

  getMeta() {
    let res = {}
    Object.keys(this).filter(k => !ignoreKeys.find(i => i == k)).forEach(k => res[k] = this[k])

    return res
  }

  /**
   * <p>Returns <a href="https://fontawesome.com/">Font Awesome</a> icon string identifier for folder.</p>
   * @param s 
   */
  getIcon(s: FourDService) {
    if (s.activeTeam.id != this.account.id) {
      return 'fa-users'
    }
    else if (!this.parent) {
      return 'fa-briefcase'
    }
    else {
      return 'fa-folder-o'
    }
  }
 
}

/**
 * <p>A file to be transformed depending on the [[TransformType]].</p>
 */
export class Dataset extends AccountableModel<Dataset>{

  @AsClass(Folder)
  folder: Folder

  @AsClass(DatasetType)
  dataType: DatasetType

  @AsClass(EPSGCodes)
  crs: EPSGCodes

  status:string

  incomplete: boolean
  incomplete_offset: number
  incomplete_seq: number
  incomplete_total: number

  initParams:any
  /**
   * <p>Array of the ID's of all completed [[Transform]]s that have been applied to this Dataset.</p>
   */
  transformIds: number[]

  /**
   * <p>Array of the uid's of all completed [[TransformType]]s that have been applied to this Dataset.</p>
   */
  transforms: string[]

  linkedInFolders:ToManyList<Dataset> = new ToManyList(Dataset,this,'linkedInFolders') 


  constructor(V: VasatConnection, args?: any, private ncur?: boolean) {
    super(V, 'Dataset', args)
  }

  /**
   * <p>Begins transform process depending on the [[TransformType]].</p>
   * @param tt [[TransformType]] you want to use
   * @param args 
   */
  applyTransform(tt: TransformType, args: any = {}, customAPI?:ProcessorAPI):Observable<any> {
     var extraURL = customAPI ? ('/' + customAPI.id) : ''
    return this.vasat.api('/api/Dataset/' + this.id + '/process/' + tt.uid + extraURL, 'post', args)
  }
  toJSON(){
    var v = super.toJSON()
    delete v.fails
    delete v.transformIds
    delete v.transforms
    delete v.canPlot
    return v
    
  }


  quoteTransform(tt: TransformType):Observable<any> {
    return this.vasat.api('/api/Dataset/' + this.id + '/process/' + tt.uid + '.quote')
  }

 
  uploadString(str:string, filename:string='data'):Observable<any>{
    var url = this.vasat.config.host + '/api/Dataset/' + this.id + '.chunked/' + filename
    var xhr = new XMLHttpRequest()

    xhr.open("POST", url + "?seq=0&offset=0&length=" + str.length + "&chunk=0&last=true")
  
    xhr.overrideMimeType("application/octet-stream")
    xhr.setRequestHeader('Content-Type','application/octet-stream')
    xhr.setRequestHeader('Authorization','Bearer ' + this.vasat.token)
    
    
    xhr.send(str);

    var sub = new Subject<Dataset>()

    xhr.onerror = (e) => {
      sub.error(e)
      sub.complete()
    }

    xhr.onreadystatechange = () => {
      if (xhr.readyState == XMLHttpRequest.DONE) {
        
        if(!xhr.responseText || xhr.responseText == ''){
          sub.error(new Error("Bad response"))
        }
        var res = JSON.parse(xhr.responseText)
        if(res.status && res.status < 400){
          sub.next(this)
        }
        else{
          sub.error(new Error(res.message))
        }
        sub.complete()
      }
    }
    return sub.asObservable()
  }
}


/**
 * <p>Stores information related to plan and pay cycle etc.</p>
 */
export class BillingSetup extends AccountableModel<BillingSetup>{

  constructor(V: VasatConnection, args?: any) {
    super(V, 'BillingSetup', args)
  }
}

/**
 * <p>Invoice</p>
 */
export class Invoice extends AccountableModel<Invoice>{

  uid: string
  invNumber: string
  receiptUrl: string
  ammount: number

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Invoice', args)
  }
}

/**
 * <p>Individual items to be charged on invoices.</p>
 */
export class InvoiceItem extends AccountableModel<InvoiceItem>{
  @AsClass(Invoice)
  invoice: Invoice

  @AsClass(BillingSetup)
  plan: BillingSetup

  units: number
  fromDate: number
  toDate: number

  constructor(V: VasatConnection, args?: any) {
    super(V, 'InvoiceItem', args)
  }
}

/**
 * <p>A group of [[Dataset]]s.</p>
 */
export class DatasetGroup extends AccountableModel<DatasetGroup>{

  @AsToManyList(Dataset)
  children: ToManyList<Dataset>

  @AsClass(DatasetGroup)
  parent: DatasetGroup

  fid: string

  constructor(V: VasatConnection, args?: any) {
    super(V, 'DatasetGroup', args)
  }

  /**
   * <p>Appends the given [[Dataset]]s.</p>
   * @param ds An array of [[Dataset]]
   */
  addChildren(ds: Dataset[]) {
    return forkJoin(ds.map(d => this.children.addObserveable(d)))
  }

  /**
   * <p>Removes the given [[Dataset]].</p>
   * @param ds An array of [[Dataset]]
   */
  delChildren(ds: Dataset[]) {
    return forkJoin(ds.map(d => this.children.removeObservable(d)))
  }

  /**
   * <p>Returns <a href=" link to vasatsearchresults ">Vasat Search Results</a>.</p>
   */
  getChildren() {
    return this.children.queryObservable()
  }

}

/**
 * <p>Transform objects are plottable objects. Transforms have several different [[TransformType]]s. The different types of transforms are: kml, laz, photo-dzi, terrain tiles*, three-d-tiles*, tms
 * <footer>Note: The types denoted by an asterix(*) are 3D models that are currently only supported by our Cesium helper class ([[FourdCesiumHelper]]).</footer></p>
 */
export class Transform extends PlottableModel<Transform>{

  @AsClass(Dataset)
  input: Dataset

  @AsToManyList(Tag)
  tags: ToManyList<Tag>

  outputs: Dataset[]

  savedAnnots: any[]

  vertical_offset: number
  metadata: any

  // Transform is a proxy for a remote resource
  isProxy:boolean

  transform?: {
    translate: any,
    heading: any,
    pitch: any,
    roll: any
  }

  /**
   * ID of transform that it can plot
   */
  canPlot:number

  pointCloudShading: any

  status: 'processing' | 'error' | 'failed' | 'finished'

  /**
   * Extra info if the transfrom is a proxy
   */
  proxyMeta:any 

  ds_type_uid:string

  ds_name?:string

  constructor(V: VasatConnection, args?: any, private ncur?: boolean) {
    super(V, 'Transform', args)
  }

  /**
   * <p>Updates values of this object with parameter object values.</p>
   * @param a The object that you want to copy values of.
   */
  set(a: any) {

    let v = super.set(a)

    if (!this.ncur && this.outputs) {
      this.outputs = this.outputs.map(c => new Dataset(this.vasat, c, true))
    }
    return v
  }

  /**
   * <p>Gets the [[Annotation]]s for this Transform object.</p>
   */
  getAnnotations() {
    return this.vasat.searchByObject(Annotation)
      .equals('transform', this.id)
      .sortBy('name')
      .limit(201)
      .queryObservable()
      .pipe(map(res => res.items))
  }

  toJSON() {
    var a = super.toJSON()
    delete a.__properties
    delete a.__detected
    delete a.issues
    return a
  }

}

/**
 * <p>Project is a collection of data, which can be shared with other [[User]]s or [[Client]]s.</p>
 */
export class Project extends PlottableModel<Project>{
  @AsToManyList(Transform)
  transforms: ToManyList<Transform>

  @AsToManyList(Transform)
  photos: ToManyList<Transform>

  @AsToManyList(Dataset)
  datasets: ToManyList<Dataset>

  /**
   * @depricated
   */
  @AsToManyList(Folder)
  folders: ToManyList<Folder>

  @AsToManyList(User)
  shareUsers: ToManyList<User>

  @AsToManyList(Client)
  shareClients: ToManyList<Client>

  @AsToManyList(Tag)
  tags: ToManyList<Tag>


  get projectFolder(){
    return this.vasat.searchByObject(Folder).equals('forProject',this.id).useCache(true).queryObservable().pipe(map(r => {
      if(r.items.length != 1)
        throw new Error("Folders for project [" + this.id + '] returned ' + r.items.length)
      return r.items[0]
    }))
  }
  constructor(V: VasatConnection, args?: any) {
    super(V, 'Project', args)
  }

  /**
   * <p>Create a new [[Folder]].</p>
   */
  addTransformsFromFolder(f: Folder, filterType?: TransformType[]): Observable<number> {
    var v = new VasatAPICall()
    var typeS = ''
    if (filterType && filterType.length) {
      filterType.forEach(f => typeS += ',' + f.id)
      typeS = typeS.substring(1)
    }

    return v.apiObservable(this.vasat, 'put', '/api/Project/' + this.id + '/addFolder/' + f.id + '/' + (filterType ? typeS : 'all'), {})
  }

  /**
   * <p>Clear all Transforms.</p>
   */
  removeAllTransforms(filterType?: TransformType[]): Observable<number> {
    var typeS = ''
    if (filterType && filterType.length) {
      filterType.forEach(f => typeS += ',' + f.id)
      typeS = typeS.substring(1)
    }

    var v = new VasatAPICall()
    return v.apiObservable(this.vasat, 'delete', '/api/Project/' + this.id + '/rmAll/' + (filterType ? typeS : 'all'))
  }

  /**
   * <p>Retrieves the list of [[TransformGroup]]s associated with this Project object.</p>
   */
  groupList(): Observable<TransformGroup[]> {

    var v = new VasatAPICall()

    return v.apiObservable(this.vasat, 'get', '/api/Project/' + this.id + '?groups=true').pipe(
      map(res => {
        var aa: any[] = res.grouped
        return aa.map(a => {
          var tg = (this.vasat.findCached(TransformGroup, a.id) || new TransformGroup(this.vasat))
          var tts: any[] = a.transforms
          tg._cachedTransforms = tts.map(t =>
            (this.vasat.findCached(Transform, t.id) || new Transform(this.vasat)).set(t)
          )
          delete a.transforms
          tg.set(a)
          return tg
        })
      })
      ,
      tap(res => 
        this.vasat.registerCacheClass(TransformGroup,res)
        
      )
    )

  }
}

/**
 * <p>A group of [[Transform]] objects.</p>
 */
export class TransformGroup extends AccountableModel<TransformGroup>{

  @AsToManyList(Transform)
  children: ToManyList<Transform>

  @AsToManyList(Transform)
  photos: ToManyList<Transform>

  @AsClass(TransformGroup)
  parent: TransformGroup

  @AsClass(Project)
  project: Project
  
  /**
   * stop using this
   * @depricated
   */
  @AsClass(Folder)
  folder: Folder
  

  @AsToManyList(Tag)
  tags: ToManyList<Tag>

  fid: string

  _cachedTransforms: Transform[]
  _photo_count: number

  layerProps:any = {}

  get sceneFolder(){
    return this.vasat.searchByObject(Folder).equals('forScene',this.id).useCache(true).queryObservable().pipe(map(r => {
      if(r.items.length != 1)
        throw new Error("Folders for scene [" + this.id + '] returned ' + r.items.length)
      return r.items[0]
    }))
  }
  
  constructor(V: VasatConnection, args?: any) {
    super(V, 'TransformGroup', args)
  }

  /**
   * <p>Adds all the images from the specified [[Folder]] to this TransformGroup. Action will also add the same photos to the [[Project]]s photos and any Photogrammetry [[Job]].</p>
   */
  addImagesFromFolder(f: Folder) {
    var v = new VasatAPICall()
    return v.apiObservable(this.vasat, 'put', '/api/TransformGroup/' + this.id + '/addFolder/' + f.id + '?access_token=' + this.vasat.token)
  }

  /**
   * <p>Remove any images associated with this TransformGroup object.</p>
   */
  removeAllImages(): Observable<number> {
    var v = new VasatAPICall()
    return v.apiObservable(this.vasat, 'delete', '/api/TransformGroup/' + this.id + '/rmAllPhotos?access_token=' + this.vasat.token)
  }

  /**
   * <p>Add [[Transform]](s) to this TransformGroup object.</p>
   * @param ds Objects to add
   */
  addChildren(ds: Transform[]) {
    return forkJoin(ds.map(d => this.children.addObserveable(d)))
  }

  /**
   * <p>Remove [[Transform]](s) to this TransformGroup object.</p>
   * @param ds Objects to remove
   */
  delChildren(ds: Transform[]) {
    return forkJoin(ds.map(d => this.children.removeObservable(d)))
  }

  addFromFiles(ds:Dataset[]):Observable<{photos:number[],transforms:number[]}> {
    return this.vasat.api('/api/TransformGroup/' + this.id + '/addDatasets','put',{"files" : ds.map(d => d.id)})
  }
  /**
   * <p>Retrieves the [[Transform]]s associated with this TransformGroup object.</p>
   */
  getChildren() {
    return this.children.queryObservable()
  }

  /**
   * <p>Gets the Shapes at a given point of this TransformGroup object.</p>
   * @param latDeg Latitude in degrees
   * @param lngDeg Longitude in degrees
   * @param height
   */
  queryShapesAtPoint(latDeg: Number, lngDeg: Number, height?: number) {
    return this.vasat.api('/api/TransformGroup/' + this.id + '.shape?lat=' + latDeg + '&lng=' + lngDeg + '&height' + (height || 0))
  }

  /**
   * <p>Parse this object to JSON format.</p>
   */
  toJSON() {
    var a = super.toJSON()
    delete a._cachedTransforms
    delete a._photo_count
    return a
  }


  isShowing(t:Transform){
    return this.layerProps['toggle_' + t.id]
  }
  setShowing(t:Transform, showing:boolean){
    this.layerProps['toggle_' + t.id] = showing
  }

  getAlpha(t:Transform){
    return this.layerProps['alpha_' + t.id]
  }
  setAlpha(t:Transform, opacity:number){
    this.layerProps['alpha_' + t.id] = opacity
  }

  getSceneFolder():Observable<Folder>{
    return this.vasat.searchByObject(Folder)
      .equals('forScene',this.id)
      .useCache(true)
      .queryObservable()
      .pipe(map(r => r.items[0]))
  }
}

/**
 * <p>Describes the different [[TransformType]]s that the [[Job]] will undertake.</p>
 */
export class JobType extends VasatModel<JobType>{

  uid:string
  
  category:string
  pluginType:'free'|'paid'

  icon:string
 
  @AsClass(TransformType)
  baseTransform: TransformType

  @AsToManyList(TransformType)
  subTransforms: ToManyList<TransformType>

  constructor(V: VasatConnection, args?: any) {
    super(V, 'JobType', args)
  }

  GUID(){
    return this.uid;
  }
  GUID_FIELD_NAME(){
    return 'uid'
  }
}

/**
 * <p>Job outlines the [[Dataset]](s) that will be transformed depending on the [[JobType]]/[[TransformType]].</p>
 */
export class Job extends AccountableModel<Job>{

  status: string

  @AsClass(JobType)
  jobType: JobType

  @AsClass(TransformGroup)
  scene: TransformGroup

  @AsClass(ProcessorAPI)
  processorAPI: ProcessorAPI

  @AsToManyList(Dataset)
  inputDatasets: ToManyList<Dataset>

  @AsToManyList(Folder)
  inputFolders: ToManyList<Folder>

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Job', args)
  }

  /**
   * <p>Queries for the associated [[JobBatch]] and reurns a <a href=" linkToVasat ">VasatSearchQuery</a> containging the results.</p>
   */
  batches(): VasatSearchQuery<JobBatch> {
    return this.vasat.searchByObject(JobBatch).equals('parent', this.id).equals('active', true)
  }

  /**
   * <p>Refresh the status of processing.</p>
   */
  refreshStatus(): Observable<any> {
    return this.vasat.api(this.vasat.pathPrefix + '/Job/process/' + this.id, 'get')
  }

  /**
   * <p>Starts the processing.</p>
   * @param t 
   * @param args 
   */
  startTransform(t: TransformType, args?: any, customAPI?:ProcessorAPI) {
     var extraURL = customAPI ? ('/' + customAPI.id) : ''

    args = args || {}
    return this.vasat.api(this.vasat.pathPrefix + '/Job/process/' + this.id + '/' + t.uid + extraURL, 'post', args)
  }

  /**
   * <p>Retrieve estimated cost of Job.</p>
   */
  getEstimatedPrice(t:TransformType): Observable<{ amount: number, unit: string }> {
    var v = new VasatAPICall()
    return v.apiObservable(this.vasat, 'get', '/api/Job/' + this.id + '/checkout?access_token=' + this.vasat.token)
  }

  /**
   * <p>Start checkout process.</p>
   * @param noonce 
   */
  makePayment(noonce: string): Observable<{ amount: number, unit: string }> {
    var v = new VasatAPICall()
    return v.apiObservable(this.vasat, 'post', '/api/Job/' + this.id + '/checkout?access_token=' + this.vasat.token, { noonce: noonce })
  }

  quoteTransform(tt: TransformType):Observable<Quote> {
    return this.vasat.api('/api/Job/' + this.id + '/process/' + tt.uid + '.quote')
  }
}

/**
 * <p>A JobBatch is a child of a [[Job]] that is undergoing a process or has already undergone transformation. A key difference to a [[Job]] is that it only has one [[TransformType]].</p>
 * <footer>Note that you will have to create a new [[Folder]] containing the outputs.</footer>
 */
export class JobBatch extends AccountableModel<JobBatch>{
  @AsClass(Job)
  parent: Job

  @AsClass(TransformType)
  transformType: TransformType

  params: any
  status: 'finished'|'failed'|'processing'

  @AsToManyList(Dataset)
  outputs: ToManyList<Dataset>

  constructor(V: VasatConnection, args?: any) {
    super(V, 'JobBatch', args)
  }

  getBatchFolder():Observable<Folder>{
    return this.vasat.searchByObject(Folder)
      .equals('forJobBatch',this.id)
      .useCache(true)
      .queryObservable()
      .pipe(map(r => r.items[0]))
  }
}

/**
 * <p>Annotations are plottable objects. Usually pins/markers/measurements.</p>
 */
export class Annotation extends PlottableModel<Annotation>{

  @AsClass(Transform)
  transform: Transform

  @AsClass(Project)
  project: Project

  uid: string

  camera: {
    position: {
      lat: number,
      lng: number,
      height: number
    },
    direction: {
      heading: number,
      pitch: number,
      roll: number
    }
  }

  style: {
    hidePin?: boolean,
    caption?: boolean
  } = {}

  // trackonus task Id
  taskIds: number[]

  photoIds: number[];

  annotationMode:string

  @AsToManyList(Tag)
  tags: ToManyList<Tag>

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Annotation', args)
  }
}



export class InviteRequest extends AccountableModel<InviteRequest> implements GUIDModel{
  uid:string
  mode:string
  expiryDate:number
  expiryConsumes:number

  @AsClass(Account)
  targetAccount: Account

  @AsClass(Project)
  targetProject:Project

  @AsClass(User)
  createdBy:User
  
  @AsClass(User)
  BindUser:User
  
  constructor(V: VasatConnection, args?: any) {
    super(V, 'InviteRequest', args)
  }
  GUID(): string {
    return this.uid;
  }
  GUID_FIELD_NAME(): string {
    return 'uid'
  }
}



export class Message extends AccountableModel<Message> implements GUIDModel{

  uid:string
  ipAddress:string
  status:string
  type:string

  @AsClass(Message)
  thread:Message

  @AsClass(InviteRequest)
  invite:InviteRequest
  
  @AsClass(JobBatch)
  jobBatch:JobBatch

  @AsClass(Transform)
  transform:Transform
  
  @AsClass(User)
  user:User

  constructor(V: VasatConnection, args?: any) {
    super(V, 'Message', args)
  }
  GUID(): string {
    return this.uid;
  }
  GUID_FIELD_NAME(): string {
    return 'uid'
  }
}